import axios from "axios";
import { AgencyDocument, AgencyDocumentTag } from "../../../db/schema";

// Admin API for the agency's policy/statute documents (server:
// routes/agencyDocumentsRoutes.ts, mounted at /api/agency/documents).
export const DOCUMENTS_API = "/api/agency/documents";

export type TagSummary = { id: string; name: string; color: string | null; sections: number };

export type AgencyDocumentRow = AgencyDocument & {
  textPages: boolean;
  tags: TagSummary[];
};

export type AgencyTagRow = AgencyDocumentTag & { sections: number };

export type SectionRow = {
  id: string;
  section_index: number;
  section_number: string | null;
  heading: string | null;
  section_path: string | null;
  page_start: number | null;
  page_end: number | null;
  char_count: number;
  excerpt: string;
  tags: { tagId: string; source: string; confidence: number | null }[];
};

export type DocumentDetailData = {
  document: AgencyDocumentRow & { droppedOverrides: number };
  sections: SectionRow[];
};

export type SearchHit = {
  sectionId: string;
  documentId: string;
  documentTitle: string;
  docType: AgencyDocument["doc_type"];
  sectionNumber: string | null;
  heading: string | null;
  sectionPath: string | null;
  pageStart: number | null;
  pageEnd: number | null;
  score: number;
  excerpt: string;
  tags: { id: string; name: string; color: string | null }[];
};

export const documentsApi = {
  list: () => axios.get<{ documents: AgencyDocumentRow[] }>(DOCUMENTS_API).then((r) => r.data.documents),
  get: (id: string) => axios.get<DocumentDetailData>(`${DOCUMENTS_API}/${id}`).then((r) => r.data),
  section: (id: string, sectionId: string) => axios.get<{ section: { content: string } }>(`${DOCUMENTS_API}/${id}/sections/${sectionId}`).then((r) => r.data.section),
  update: (id: string, body: { title?: string; docType?: AgencyDocument["doc_type"] }) => axios.patch<{ document: AgencyDocument }>(`${DOCUMENTS_API}/${id}`, body).then((r) => r.data.document),
  reprocess: (id: string) => axios.post(`${DOCUMENTS_API}/${id}/reprocess`),
  remove: (id: string) => axios.delete(`${DOCUMENTS_API}/${id}`),
  viewUrl: (id: string, download = false) => axios.post<{ url: string }>(`${DOCUMENTS_API}/${id}/view-url`, { download }).then((r) => r.data.url),
  setSectionTags: (id: string, sectionId: string, body: { add?: string[]; remove?: string[]; create?: { name: string; description?: string }[] }) =>
    axios.patch<{ sectionId: string; tags: SectionRow["tags"] }>(`${DOCUMENTS_API}/${id}/sections/${sectionId}/tags`, body).then((r) => r.data),
  tags: () => axios.get<{ tags: AgencyTagRow[] }>(`${DOCUMENTS_API}/tags`).then((r) => r.data.tags),
  createTag: (body: { name: string; description?: string | null; color?: string }) => axios.post<{ tag: AgencyTagRow }>(`${DOCUMENTS_API}/tags`, body).then((r) => r.data.tag),
  updateTag: (tagId: string, body: { name?: string; description?: string | null; color?: string }) => axios.patch<{ tag: AgencyDocumentTag }>(`${DOCUMENTS_API}/tags/${tagId}`, body).then((r) => r.data.tag),
  deleteTag: (tagId: string) => axios.delete(`${DOCUMENTS_API}/tags/${tagId}`),
  mergeTag: (tagId: string, intoTagId: string) => axios.post(`${DOCUMENTS_API}/tags/${tagId}/merge`, { intoTagId }),
  search: (q: string, opts: { docType?: string; tagIds?: string[] } = {}) =>
    axios
      .get<{ sections: SearchHit[] }>(`${DOCUMENTS_API}/search`, { params: { q, docType: opts.docType || undefined, tagIds: opts.tagIds?.length ? opts.tagIds.join(",") : undefined } })
      .then((r) => r.data.sections),
};

export const DOC_TYPE_LABELS: Record<AgencyDocument["doc_type"], string> = {
  policy: "Policy",
  statute: "Statute",
  other: "Other",
};

export const DOC_TYPE_OPTIONS = [
  { value: "policy", label: "Policy or procedure" },
  { value: "statute", label: "Statute or ordinance" },
  { value: "other", label: "Other" },
];

export const STAGE_LABELS: Record<string, string> = {
  extract: "reading pages",
  section: "splitting sections",
  tag: "tagging sections",
  embed: "indexing",
  done: "done",
};

export function formatBytes(n: number | null | undefined): string {
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 2 : 1)} MB`;
}
